import React, { useEffect } from "react";
import { ChibanViewer } from "./ChibanViewer";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { GetUserMapConfigAPI } from "@/features/map/api/getUserMapConfigAPI";
import { LatLng } from "leaflet";

import { DEFAULT_MAP_LATLNG } from "@/utils/mapLocations";
import { useLocation, useNavigate } from "react-router-dom";
import { useMapServiceContract } from "./hooks/useMapServiceContract";

import type {
  SavedMapPlotDataWithRequestId,
  SavedMapPlotDataListWithRequestId,
} from "./types/component";

interface LocationState {
  // 地図プロット詳細画面の座標マーカーから遷移時の単一プロットデータ
  savedMapPlotData?: SavedMapPlotDataWithRequestId;
  // 「保存した物件リスト一覧」モーダルを自動オープンするフラグ
  openSavedPlotModal?: boolean;
  // プロット詳細画面の「プロットする」ボタンから遷移時に地図に表示するプロットデータ（複数件）
  plotDetailsToMap?: SavedMapPlotDataListWithRequestId;
  // 読み込んだプロットデータのデータセット情報（画面上部のLoadedMapPlotBarで「◯◯データセット」と表示される）
  datasetInfo?: Array<{ id: number; name: string }>;
}

export const ChibanViewerAccessControl: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // ユーザーの地図初期設定をAPIから取得
  const { data: userMapConfig, isLoading: isMapConfigLoading } =
    GetUserMapConfigAPI();

  // 地図サービス契約情報をSWRで取得（5分ごとに自動更新）
  const { data: contractInfo, isLoading: isContractLoading } =
    useMapServiceContract();

  // プロット詳細画面(/map-plot-request/:id)から地図画面(/map)へ遷移時にlocation.stateで渡されるデータ
  // 以下の2つのケースがある:
  // 1. 「戻る」ボタン押下時: openSavedPlotModal=true で「保存した物件リスト一覧」モーダルを自動オープン
  // 2. 「プロットする」ボタン押下時: plotDetailsToMap と datasetInfo でプロットデータを地図に反映
  //
  // location.stateは画面遷移時にしか存在しないため、初回レンダリング時にReact stateに保存する
  const locationState = location.state as LocationState;
  const [savedMapPlotData] = React.useState(locationState?.savedMapPlotData);
  const [openSavedPlotModal] = React.useState(
    locationState?.openSavedPlotModal
  );
  const [plotDetailsToMap] = React.useState(locationState?.plotDetailsToMap);
  const [datasetInfo] = React.useState(locationState?.datasetInfo);

  // マウント直後にlocation.stateをクリアする
  // 理由: location.stateはブラウザの履歴に残るため、クリアしないと以下の問題が発生する:
  //   例) プロット詳細画面 → 地図画面(プロットデータ表示) → 他画面 → ブラウザバック で地図画面に戻ると
  //       前回のプロットデータが再度location.stateから読み込まれ、意図しないデータが表示される
  // React stateに既に保存済みなので、location.stateをクリアしても問題ない
  useEffect(() => {
    const state = location.state as LocationState | null;
    if (state && Object.keys(state).length > 0) {
      navigate(location.pathname, {
        replace: true,
        state: {},
      });
    }
  }, [location.pathname, location.state, navigate]);

  // ユーザー設定、契約情報の取得が完了するまでローディング画面を表示
  if (isMapConfigLoading || isContractLoading || !contractInfo) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  /**
   * 地図の初期表示位置を決定（優先順位順）
   * 1. 地図プロット詳細画面から遷移した場合 → そのピンの位置を中心に表示
   * 2. ユーザーが設定した初期位置がある場合 → その位置を中心に表示
   * 3. 上記がない場合 → システムのデフォルト位置を表示
   */
  const initialLatLng = ((): LatLng => {
    if (
      savedMapPlotData?.substituteLatitude != null &&
      savedMapPlotData?.substituteLongitude != null
    ) {
      return new LatLng(
        savedMapPlotData.substituteLatitude,
        savedMapPlotData.substituteLongitude
      );
    }
    if (userMapConfig?.mapInitLat && userMapConfig?.mapInitLon) {
      return new LatLng(userMapConfig.mapInitLat, userMapConfig.mapInitLon);
    }
    return DEFAULT_MAP_LATLNG;
  })();

  // 初期座標と必要なデータをPropsとして渡し、地図コンポーネントを描画
  return (
    <>
      <ChibanViewer
        initialLatLng={initialLatLng}
        savedMapPlotData={savedMapPlotData}
        openSavedPlotModal={openSavedPlotModal}
        plotDetailsToMap={plotDetailsToMap}
        datasetInfo={datasetInfo}
        hasMapViewingPermission={contractInfo.hasMapViewingPermission}
        hasZenrinContract={contractInfo.hasZenrinContract}
        hasGeospaceContract={contractInfo.hasGeospaceContract}
      />
    </>
  );
};
